<template>
    <div class="page equivalent-clearcut-area">
        <div class="map-view">
            <mapbox-gl-map
                id="map"
                :options="getMapOptions()"
                zoom-control
                @load="onMapLoad"
                @click="onMapClick"
                @moveend="onMapMoveend"
                @error="onMapError"
            />
            <map-popup
                v-if="displayHowToPopup"
                id="map-popup-eca-how-to"
                class="how-to-popup has-text-centered"
                :title="howToPopup.title"
                @close-popup="closeHowToPopup"
            >
                <p>
                    {{ howToPopup.content }}
                </p>
            </map-popup>
            <map-locator-scale
                v-if="map"
                :map="map"
            />
            <transition name="fade">
                <map-popup
                    v-if="selectedWatershed !== null"
                    id="map-popup-eca"
                    :title="selectedWatershed.name"
                    @close-popup="clearSelectedFeatures"
                >
                    <p>
                        Watershed #{{ selectedWatershed.wfi }}
                    </p>
                    <p class="has-text-centered">
                        <b-button
                            class="is-secondary"
                            :loading="reportLoading"
                            :disabled="reportError"
                            @click="showReport"
                        >
                            {{ reportError ? 'Report Unavailable' : 'Show Report' }}
                        </b-button>
                    </p>
                </map-popup>
            </transition>
            <search-bar
                :search-types="searchTypes"
                :loading-results="loadingSearch"
                :search-results="searchResults"
                :geolocate-tooltip-options="geolocateTooltipOptions"
                @select="selectSearchResult"
                @geolocation="goToLocation"
                @input="searchInput"
                @typing="searchBarTyping"
            />
            <style-changer
                v-if="map"
                :selected="mapStyle.label"
                :options="mapStyles"
                @change="changeMapStyle"
            />
            <transition name="fade-fast">
                <activity-indicator
                    v-if="loading"
                    class="has-text-secondary"
                />
            </transition>
        </div>

        <equivalent-clearcut-area-report
            v-if="selectedWatershed !== null"
            :lng-lat="clickLngLat"
            :watershed-info="selectedWatershed"
            @report-data-available="whenReportDataAvailable"
            @report-data-error="whenReportDataError"
        />
    </div>
</template>

<script>
import ActivityIndicator from '@foundryspatial/activity-indicator';
import MapLocatorScale from '@foundryspatial/map-locator-scale';
import MapPopup from '@foundryspatial/map-popup';
import mapStyleSwitcherMixin from '@foundryspatial/map-style-changer/mixin';
import MapboxGlMap from '@foundryspatial/mapbox-gl-map';
import mapCoordinator from '@foundryspatial/mixin-map-coordinator';
import saveMapBounds from '@foundryspatial/mixin-save-map-bounds';
import SearchBar from '@foundryspatial/search-bar';

import scssVars from '@/assets/scss/_variables.scss';
import EquivalentClearcutAreaReport from '@/components/EquivalentClearcutAreaReport/EquivalentClearcutAreaReport.vue';
import { EMPTY_SOURCE_DATA } from '@/constants/geojson.js';
import { EQUIVALENT_CLEARCUT_AREA as MAP_STYLES } from '@/constants/mapStyles.js';
import {
    API_CRITICAL_ERROR,
    API_ERROR_WATERSHED_DATA,
    NO_DATA_AT_LOCATION,
    SELECTION_OUTSIDE_STUDY_AREA,
} from '@/constants/messages.js';
import {
    GEOLOCATE_TOOLTIP_LABEL,
    SEARCH_TYPE_GEONAMES,
    SEARCH_TYPE_GEONAMES_PLACEHOLDER,
    SEARCH_TYPE_LAT_LONG,
    SEARCH_TYPE_LAT_LONG_PLACEHOLDER,
    SEARCH_TYPE_UTM,
    SEARCH_TYPE_UTM_PLACEHOLDER,
    SEARCH_TYPE_WFI,
    SEARCH_TYPE_WFI_PLACEHOLDER,
} from '@/constants/search.js';
import searchHelpers from '@/mixins/searchHelpers.js';

export default {
    name: 'EquivalentClearcutArea',

    components: {
        ActivityIndicator,
        EquivalentClearcutAreaReport,
        MapboxGlMap,
        MapPopup,
        SearchBar,
        MapLocatorScale,
    },

    mixins: [
        mapCoordinator,
        saveMapBounds,
        searchHelpers,
        mapStyleSwitcherMixin,
    ],

    data: () => ({
        map: null,
        showHowToPopup: true,
        selectedWatershed: null,
        clickLngLat: null,
        reportLoading: false,
        reportError: false,
        gettingWatershed: false,
        searchTypes: [
            {
                label: SEARCH_TYPE_GEONAMES,
                placeholder: SEARCH_TYPE_GEONAMES_PLACEHOLDER,
                icon: 'magnify',
            },
            {
                label: SEARCH_TYPE_LAT_LONG,
                placeholder: SEARCH_TYPE_LAT_LONG_PLACEHOLDER,
                icon: 'map-marker',
            },
            {
                label: SEARCH_TYPE_UTM,
                placeholder: SEARCH_TYPE_UTM_PLACEHOLDER,
                icon: 'map-marker',
            },
            {
                label: SEARCH_TYPE_WFI,
                placeholder: SEARCH_TYPE_WFI_PLACEHOLDER,
                icon: 'map-marker',
            },
        ],

        geolocateTooltipOptions: {
            label: GEOLOCATE_TOOLTIP_LABEL,
            position: 'is-bottom',
            type: 'is-info',
        },

        mapStyles: [
            {
                label: 'Base Map',
                styleUrl: MAP_STYLES.BASE.URL,
            },
            {
                label: 'Satellite',
                styleUrl: MAP_STYLES.SATELLITE.URL,
            },
        ],

        howToPopup: {
            title: 'Equivalent Clearcut Area Reporting',
            content: 'Click on a stream, lake, or river to see the equivalent clearcut area associated with that watershed and generate a detailed report about it.',
        },
    }),

    computed: {
        loading() {
            return this.map === null || this.gettingWatershed;
        },

        displayHowToPopup() {
            return this.showHowToPopup && this.selectedWatershed === null;
        },
    },

    methods: {
        /**
         * Hide How-To popup
         */
        closeHowToPopup() {
            this.showHowToPopup = false;
        },

        /**
         * Handle selected search results
         *
         * @param {object} result - option from the search results.
         */
        selectSearchResult(result) {
            this.clearSelectedFeatures();
            // conciscode is a value from the DB which describes the type of search result
            // in our case we want to handle specific types a bit differently from
            // all the rest. Some examples: `CITY`, `VILG`, `LAKE`, etc.
            // it's sort of like a "category". I dunno where the name comes from
            if (['WATERSHED', 'COORDINATES'].includes(result.conciscode)) {
                // simulate a click on the result's coordinates
                this.getWatershedAtLngLatZoom({ lng: result.lng, lat: result.lat }, 0);
            } else {
                this.goToLocation(result.geojson, result.zoom);
                if (['SW-SHORT', 'GW-SHORT', 'SW-LONG', 'GW-LONG'].includes(result.conciscode)) {
                    // select this licence if we can, too.
                    if ('fs_id' in result.metadata) {
                        this.selectLicence(result.metadata.fs_id);
                    }
                }
            }

            // clear search results
            this.searchResults = [];
        },

        /**
         * Builds and returns a Mapbox GL map config object.
         *
         * @returns {object} - Mapbox GL map options
         */
        getMapOptions() {
            const mapOptions = {
                style: MAP_STYLES.BASE.URL,
                bounds: [[-128.55, 52.65], [-123.87, 55.61]],
                fitBoundsOptions: {
                    padding: 35,
                },
            };

            // load saved map view
            const savedBounds = this.loadMapBounds();
            if (savedBounds !== null) {
                // bounds will override center/zoom
                mapOptions.bounds = savedBounds;
            }

            return mapOptions;
        },

        /**
         * Add a source and layer if they don't exist in the current style. This is necessary for when we switch a
         * style and need to make sure the source exists in the style. Also check to make sure the source or layer
         * exists before we try to add them here too.
         */
        addWatershedSourceIfNeeded() {
            // add the watershed-polygon source & layer
            if (!this.map.getSource('watershed-polygon')) {
                this.map.addSource('watershed-polygon', {
                    type: 'geojson',
                    data: EMPTY_SOURCE_DATA,
                });
            }
            if (!this.map.getLayer('watershed-polygon')) {
                this.map.addLayer({
                    id: 'watershed-polygon',
                    type: 'fill',
                    source: 'watershed-polygon',
                    paint: {
                        'fill-color': scssVars.colorWatershedQuery,
                        'fill-opacity': 0.5,
                        'fill-outline-color': scssVars.colorWatershedQueryBorder,
                    },
                }, 'change-points-1985');
            }
        },

        /**
         * Add interaction handlers when the map loads.
         *
         * @param {MapDataEvent} loadEvent - Mapbox GL load event object
         * @param {mapboxgl.Map} map - Mapbox GL map object
         */
        onMapLoad(loadEvent, map) {
            this.map = map;

            // add empty source layer for watershed polygon
            this.addWatershedSourceIfNeeded(map);
        },

        /**
         * Set the existing watershed-polygon layer source and clears the polygon if null.
         *
         * @param {object | null} geometry - polygon geojson (or null to clear)
         */
        setWatershedPolygon(geometry) {
            this.addWatershedSourceIfNeeded(this.map);

            const source = this.map.getSource('watershed-polygon');
            let newData;

            if (geometry === null) {
                // set the source data to an invisible feature
                newData = EMPTY_SOURCE_DATA;
            } else {
                newData = geometry;
                this.goToLocation(newData, 0);
            }

            source.setData(newData);
        },

        /**
         * Get a watershed at a specific point and zoom level
         *
         * @param {object} lngLat - an object containing `lng` and `lat` properties.
         * @param {number} zoom - zoom level of the map - used in searchDistance calculations.
         */
        async getWatershedAtLngLatZoom(lngLat, zoom) {
            try {
                this.gettingWatershed = true;
                // query for watershed data at the click coordinates
                const watershed = await this.$api.getEcaWatershedByCoords(lngLat, zoom);

                // show a message if no watershed is found at this location
                if (watershed === null) {
                    this.$handleError('', NO_DATA_AT_LOCATION, false, {
                        type: 'is-warning',
                    });
                    return;
                }

                // hide how to popup
                this.showHowToPopup = false;

                // select watershed feature
                this.selectedWatershed = watershed;
                this.clickLngLat = lngLat;
                this.reportLoading = true;

                // add watershed polygon to the map
                this.setWatershedPolygon(this.selectedWatershed.geom);
            } catch (e) {
                this.$handleError(e, API_ERROR_WATERSHED_DATA);
            } finally {
                this.gettingWatershed = false;
            }
        },

        /**
         * Handles 'click' events anywhere on the map.
         *
         * @param {MapMouseEvent} clickEvent - Mapbox GL click event object
         * @param {mapboxgl.Map} map - Mapbox GL map object
         */
        async onMapClick(clickEvent, map) {
            // only allow one query at a time
            if (this.loading) return;

            // clear selected features
            this.clearSelectedFeatures();

            // check if the click is outside the study area
            const STUDY_AREA_MASK_ID = 'nadina-mapbox-mask';
            // query will return the mask vector tile if the click is outside of the study area
            const featuresOutsideStudyArea = map.queryRenderedFeatures(
                clickEvent.point,
                { layers: [STUDY_AREA_MASK_ID] },
            );
            if (featuresOutsideStudyArea.some((feature) => feature.layer.id === STUDY_AREA_MASK_ID)) {
                // show a message and cancel the click handler
                this.$handleError('', SELECTION_OUTSIDE_STUDY_AREA);
                return;
            }

            await this.getWatershedAtLngLatZoom(clickEvent.lngLat, map.getZoom());
        },

        /**
         * Handle map 'moveend' events when map completes view transition.
         *
         * @param {DragEvent} moveendEvent - Mapbox GL moveend event object
         * @param {MapboxGLMap} map - Mapbox GL map object
         */
        onMapMoveend(moveendEvent, map) {
            // persist map view
            this.saveMapBounds(map);
        },

        /**
         * When report data is available, we know the report is done loading.
         */
        whenReportDataAvailable() {
            this.reportLoading = false;
        },

        /**
         * Set flags appropriately when there is an error with retrieving report data.
         */
        whenReportDataError() {
            this.reportError = true;
            this.reportLoading = false;
        },

        /**
         * Scrolls the page to show the report element.
         */
        showReport() {
            document
                .getElementById('equivalent-clearcut-area-report')
                .scrollIntoView();
        },

        /**
         * Unselect any watershed and forest disturbances map features.
         */
        clearSelectedFeatures() {
            // clear the watershed polygon
            this.setWatershedPolygon(null);

            this.selectedWatershed = null;
            this.clickLngLat = null;
        },

        /**
         * Pass Mapbox GL errors to global error handler to display a message.
         *
         * @param {Event} errorEvent - Mapbox GL error event object
         */
        onMapError(errorEvent) {
            // pass to error handler as a critical error
            this.$handleError(errorEvent, API_CRITICAL_ERROR, true);
        },

        onMapStyleChange() {
            if (this.selectedWatershed) {
                this.setWatershedPolygon(this.selectedWatershed.geom);
            }
        },
    },
};
</script>

<style lang="scss">
.equivalent-clearcut-area {
    .how-to-popup {
        p {
            margin: 2rem 0;
        }
    }
}
</style>
